import { useUnsavedPromptInModal } from '@common/UnsavedPromptInModal.hooks';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { Button, ComponentType, LoadingButton, ModalBodyExtras, useModal } from '@gcl/react-core';
import { IProjectTeam } from '@projectTeam/projectTeam.models';
import * as React from 'react';
import { ITeamFormProps, TeamForm } from './TeamForm';

export interface IUseTeamModalProps {
  onConfirm: (team?: IProjectTeam) => void;
}

export interface IRenderTeamModalProps extends ITeamFormProps {
  isSubmitLoading: boolean;
  isSubmitError: boolean;
  isSubmitSuccess: boolean;
  shouldDisplay?: boolean;
}

export const useTeamModal = ({ onConfirm }: IUseTeamModalProps) => {
  const { openPortal, isOpen, closePortal, renderModal } = useModal();

  const renderTeamModal = ({
    team,
    isSubmitLoading,
    isSubmitError,
    isSubmitSuccess,
    shouldDisplay = true,
    ...teamFormProps
  }: IRenderTeamModalProps) => {
    const { isFormDirty, setIsFormDirty, confirmUnsavedPrompt } = useUnsavedPromptInModal();
    const { useFormMethods } = teamFormProps;
    const {
      formState: { isDirty },
    } = useFormMethods;

    React.useEffect(() => {
      setIsFormDirty(isDirty);
    }, [isDirty]);

    if (!shouldDisplay) return <></>;

    return (
      isOpen &&
      renderModal({
        onHide: () => {
          useFormMethods.reset({});
        },
        backdrop: isFormDirty ? 'static' : undefined,
        large: true,
        title: `${team && isPositiveNumber(team.id) ? 'Edit' : 'Add'} Team`,
        onRenderBody: (bodyExtras?: ModalBodyExtras) => {
          return <TeamForm team={team} {...teamFormProps} />;
        },
        onRenderFooter: (onHide?: () => void, bodyExtras?: ModalBodyExtras) => {
          return (
            <>
              <LoadingButton
                type={ComponentType.Primary}
                onClick={() => {
                  onConfirm(team);
                }}
                isLoading={isSubmitLoading}
                isError={isSubmitError}
                isSuccess={isSubmitSuccess}
                timeToResetSuccessState={3000}
                timeToResetErrorState={5000}
                prefixIconName="FolderCheck"
              >
                Save
              </LoadingButton>
              <Button
                prefixIconName="ChevronLeft"
                type={ComponentType.Secondary}
                onClick={() => {
                  if (confirmUnsavedPrompt() && onHide) {
                    onHide();
                  }
                }}
                style={{ margin: '10px' }}
              >
                Cancel
              </Button>
            </>
          );
        },
      })
    );
  };
  return {
    openPortal,
    closePortal,
    renderModal: renderTeamModal,
  };
};
